import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaCamera } from 'react-icons/fa';
import '../styles/ConversionHooks.css';

const getFooterElement = () =>
  document.getElementById('site-footer') || document.querySelector('footer.footer');

const ConversionHooks = () => {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin') || location.pathname === '/admin-login';

  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  const [hideFloatingCTAForFooter, setHideFloatingCTAForFooter] = useState(false);

  /** True when any part of the footer is visible in the viewport */
  const checkFooterVisible = useCallback(() => {
    const footer = getFooterElement();
    if (!footer) {
      setHideFloatingCTAForFooter(false);
      return;
    }
    const { top, bottom } = footer.getBoundingClientRect();
    const vh = window.innerHeight;
    setHideFloatingCTAForFooter(top < vh && bottom > 0);
  }, []);

  useEffect(() => {
    if (isAdminPage) return;

    const onScrollOrResize = () => {
      const threshold = Math.max(240, Math.min(800, Math.round(window.innerHeight * 0.8)));
      setShowFloatingCTA(window.scrollY > threshold);
      checkFooterVisible();
    };

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    onScrollOrResize();

    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isAdminPage, checkFooterVisible]);

  useEffect(() => {
    if (isAdminPage) {
      setHideFloatingCTAForFooter(false);
      return undefined;
    }

    let observer;
    let retryTimer;

    const attachObserver = () => {
      const footer = getFooterElement();
      if (!footer) return false;

      checkFooterVisible();

      if (typeof IntersectionObserver === 'undefined') return true;

      observer = new IntersectionObserver(
        ([entry]) => {
          setHideFloatingCTAForFooter(Boolean(entry?.isIntersecting));
        },
        { root: null, threshold: 0 }
      );
      observer.observe(footer);
      return true;
    };

    if (!attachObserver()) {
      retryTimer = window.setTimeout(attachObserver, 200);
    }

    return () => {
      if (retryTimer) window.clearTimeout(retryTimer);
      observer?.disconnect();
    };
  }, [isAdminPage, location.pathname, checkFooterVisible]);

  return (
    <>
      {!isAdminPage && showFloatingCTA && !hideFloatingCTAForFooter && (
        <div className="floating-cta-group">
          <a
            href={`https://wa.me/8801601719735?text=${encodeURIComponent('Hi! I\'m enquiring through the Khuyung WedStories website. I\'d like to know more about your wedding photography & cinematography services.')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="floating-cta-button floating-cta-whatsapp"
            aria-label="Chat on WhatsApp"
          >
            <span className="floating-cta-icon" aria-hidden>
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884z"/></svg>
            </span>
            <span className="floating-cta-text">
              <strong>WhatsApp</strong>
              <small>Chat with us</small>
            </span>
          </a>
          <Link to="/book" className="floating-cta-button">
            <span className="floating-cta-icon"><FaCamera /></span>
            <span className="floating-cta-text">
              <strong>Book Your Day</strong>
              <small>Limited Slots Available</small>
            </span>
          </Link>
        </div>
      )}
    </>
  );
};

export default ConversionHooks;
